import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  UserFilterForm,
  UserFilterFormRef,
  UserFilterFormValues,
} from "@components/Forms/UserFilterForm/UserFilterForm";
import { UsersTable } from "@components/Tables";
import { UsersTableColumnCustomization } from "@components/Tables/UsersTable/UI/UsersTableColumnCustomization";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import { UsersTableConfig } from "@components/Tables/UsersTable/UsersTableConfig";
import {
  DEFAULT_USER_TABLE_COLUMN_ORDER,
  DEFAULT_USER_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/UsersTable/UI/UsersTableColumns";

type Props = {
  initialUserTablePageIndex?: number;
};

const getDefaultFilterValues = (): UserFilterFormValues => ({
  nameSearch: "",
  emailSearch: "",
});

const areFiltersActive = (filterValues: UserFilterFormValues): boolean => {
  return Boolean(
    (filterValues.nameSearch && filterValues.nameSearch.trim()) ||
      (filterValues.emailSearch && filterValues.emailSearch.trim()),
  );
};

export const UsersCard = ({ initialUserTablePageIndex = 0 }: Props) => {
  const navigate = useNavigate();
  const [filterValues, setFilterValues] = useState<UserFilterFormValues>(getDefaultFilterValues());
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnCustomizationOpen, setIsColumnCustomizationOpen] = useState(false);

  const filterFormRef = useRef<UserFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues);
  }, [filterValues]);

  const handleUserClientClick = useCallback(() => {
    navigate(routes.portal.users.create.$path(), { viewTransition: true });
  }, [navigate]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: UserFilterFormValues) => {
      // Called when form is submitted (Enter key)
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setIsFilterPanelOpen(false);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnCustomizationOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnCustomizationOpen(false);
  }, []);

  const isFiltersActive = filtersActive;

  // Export functionality - simplified to export current page only to avoid API limit issues
  const handleExportCSV = async () => {
    try {
      // Create a simple export function that uses the fetcher directly
      const apiUrl = import.meta.env.VITE_API_URL_GRAPHQL as string;
      const query = `
        query ExportUsers($first: Int!, $offset: Int!, $name_Icontains: String, $email_Icontains: String) {
          users(first: $first, offset: $offset, name_Icontains: $name_Icontains, email_Icontains: $email_Icontains) {
            totalCount
            edges {
              node {
                id
                name
                email
                createdAt
              }
            }
          }
        }
      `;

      const allUsers: { id: string; name: string; email: string; createdAt: string }[] = [];
      let hasMoreData = true;
      let offset = 0;
      const pageSize = 100; // Use the maximum allowed limit

      // Fetch all users with pagination
      while (hasMoreData) {
        const response = await fetch(apiUrl, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query,
            variables: {
              first: pageSize,
              offset: offset,
              name_Icontains: filterValues.nameSearch || undefined,
              email_Icontains: filterValues.emailSearch || undefined,
            },
          }),
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        const users = result.data?.users?.edges
          .map((edge: { node?: { id: string; name: string; email: string; createdAt: string } }) => edge?.node)
          .filter((user: { id: string; name: string; email: string; createdAt: string } | undefined): user is { id: string; name: string; email: string; createdAt: string } => !!user) ?? [];

        allUsers.push(...users);

        // Check if we have more data
        const totalCount = result.data?.users?.totalCount ?? 0;
        hasMoreData = offset + pageSize < totalCount;
        offset += pageSize;
      }

      if (allUsers.length === 0) {
        return;
      }

      const csvContent = [
        // Header row
        ['Name', 'Email', 'Created'].join(','),
        // Data rows
        ...allUsers.map((user: { id: string; name: string; email: string; createdAt: string }) =>
          [
            `"${user.name || ''}"`,
            `"${user.email || ''}"`,
            `"${new Date(user.createdAt).toLocaleDateString()}"`,
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  return (
    <TableColumnSettingsProvider
      tableId={UsersTableConfig.tableId}
      defaultColumnSettings={DEFAULT_USER_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_USER_TABLE_COLUMN_ORDER}
    >
      <Card grow>
        <Card.Header withBottomPadding>
          <Card.Header.Title>Staff list</Card.Header.Title>
          <Card.Header.Actions>
            <Box direction="row" gap={8} align="center">
              {isFiltersActive && (
                <Text size={12} color={"textSecondary"}>
                  Filters are applied
                </Text>
              )}
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={handleFilterClick}
                outline={"sm"}
                showBubble={isFiltersActive}
                tooltip="Show filters"
              />
              <IconButton
                size="sm"
                icon="faTableColumns"
                onClick={handleColumnSettingsClick}
                outline={"sm"}
                tooltip="Show/Hide columns"
              />
              <IconButton
                size="sm"
                icon="faFileCsv"
                onClick={handleExportCSV}
                outline={"sm"}
                tooltip="Export table data to CSV"
              />
              <Button size="sm" icon="faPlus" onClick={handleUserClientClick}>
                Create staff user
              </Button>
            </Box>
          </Card.Header.Actions>
        </Card.Header>
        <Card.Body noPadding grow>
          <UsersTable
            initialPageIndex={initialUserTablePageIndex}
            nameSearch={filterValues.nameSearch}
            emailSearch={filterValues.emailSearch}
          />
        </Card.Body>
      </Card>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="User Filters"
      >
        <UserFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <UsersTableColumnCustomization
        isOpen={isColumnCustomizationOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
